if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.submitButton = this.querySelector('.sticky-atc__submit');
        this.mainSubmitButton = null;
        this.observer = null;
        this.headerObserver = null;
        this.headerClassObserver = null;
        this.buttonObserver = null;
        this.header = null;
        this.productInfoContainer = null;
        this.productInfoResizeObserver = null;
        this.handleResize = this.handleResize.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.variantChangeUnsubscriber = undefined;
        this.isVisible = false;
        this.moved = false;
      }

      connectedCallback() {
        this.moveToBody();
        this.mainSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        if (!this.mainSubmitButton || !this.submitButton || window.disableStickyAtc) {
          this.remove();
          return;
        }

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.headerObserver?.disconnect();
        this.headerClassObserver?.disconnect();
        this.productInfoResizeObserver?.disconnect();
        this.buttonObserver?.disconnect();
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('scroll', this.handleScroll);
        this.variantChangeUnsubscriber?.();
        this.setBodyVisibleState(false);
      }

      moveToBody() {
        if (this.moved) return;
        this.moved = true;
        const existingStickyAtc = document.querySelector('body > sticky-atc');
        if (existingStickyAtc) {
          existingStickyAtc.replaceWith(this);
        } else {
          document.body.appendChild(this);
        }

        this.mainSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        if (!this.mainSubmitButton || !this.submitButton || window.disableStickyAtc) {
          this.remove();
          return;
        }

        requestAnimationFrame(() => {
          this.submitButton.addEventListener('click', this.handleSubmitClick.bind(this));
          this.setupIntersectionObserver();
          this.setupProductInfoObserver();
          this.setupButtonObserver();
          this.syncSubmitButton();
        });
      }

      getAbsoluteTop(element) {
        let top = 0;
        while (element) {
          top += element.offsetTop;
          element = element.offsetParent;
        }
        return top;
      }

      setupIntersectionObserver() {
        this.observer = new IntersectionObserver(
          (entries) => {
            const isIntersecting = entries[0]?.isIntersecting ?? true;
            this.setVisibleState(!isIntersecting);
          },
          {
            root: null,
            threshold: 0
          }
        );

        this.observer.observe(this.mainSubmitButton.closest(`form#product-form-${this.sectionId}`));
      }

      syncHeaderOffset() {
        requestAnimationFrame(() => {
          const isDesktop = window.matchMedia('(min-width: 1200px)').matches;
          if (!isDesktop || !this.header) {
            this.style.removeProperty('top');
            this.isInsertedHeader = false;
            return;
          }
          this.style.top = '0px';
          return;
        });
      }

      handleResize() {
        this.syncHeaderOffset();
        this.syncSubmitButtonWidth();
        this.setBodyVisibleState(this.isVisible);
      }

      getProductInfoContainer() {
        const productInfo = document.querySelector(`product-info[data-section="${this.sectionId}"]`);
        if (!productInfo) return null;

        return (
          productInfo.querySelector('.product__info-container.product__column-sticky') ||
          productInfo.querySelector('.product__info-container')
        );
      }

      setupProductInfoObserver() {
        this.productInfoContainer = this.getProductInfoContainer();
        if (!this.productInfoContainer) return;

        this.syncSubmitButtonWidth();

        this.productInfoResizeObserver = new ResizeObserver(() => {
          this.syncSubmitButtonWidth();
        });

        this.productInfoResizeObserver.observe(this.productInfoContainer);
      }

      syncSubmitButtonWidth() {
        if (!this.submitButton) return;

        const isDesktop = window.matchMedia('(min-width: 1200px)').matches;
        if (!isDesktop) {
          this.submitButton.style.removeProperty('width');
          return;
        }

        const infoContainer = this.productInfoContainer || this.getProductInfoContainer();
        if (!infoContainer) return;

        this.productInfoContainer = infoContainer;
        const width = infoContainer.offsetWidth;
        this.submitButton.style.width = `${width}px`;
      }

      handleScroll() {
        if (!this.isVisible) return;

        const isDesktop = window.matchMedia('(min-width: 1200px)').matches;
        if (!isDesktop) return;

        this.syncHeaderOffset();
      }

      setupButtonObserver() {
        this.buttonObserver = new MutationObserver(() => {
          this.syncSubmitButton();
        });

        this.buttonObserver.observe(this.mainSubmitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true
        });
      }

      handleVariantChange({ data }) {
        if (!data || data.sectionId !== this.sectionId) return;

        this.updateStickyBlock(data.html, 'sticky-atc-product');
        this.updateStickyBlock(data.html, 'sticky-atc-price');
        this.syncSubmitButton();
      }

      updateStickyBlock(html, blockId) {
        const source = html?.getElementById(`${blockId}-${this.sectionId}`);
        const destination = document.getElementById(`${blockId}-${this.sectionId}`);

        if (source && destination) {
          destination.innerHTML = source.innerHTML;
        }
      }

      handleSubmitClick() {
        if (!this.mainSubmitButton) return;
        if (this.mainSubmitButton.disabled) return;
        if (this.mainSubmitButton.getAttribute('aria-disabled') === 'true') return;

        this.mainSubmitButton.click();
      }

      syncSubmitButton() {
        if (!this.mainSubmitButton || !this.submitButton) return;

        const mainText = this.mainSubmitButton.querySelector('span');
        const stickyText = this.submitButton.querySelector('span');
        const mainSpinner = this.mainSubmitButton.querySelector('.loading__spinner');
        const stickySpinner = this.submitButton.querySelector('.loading__spinner');

        if (mainText && stickyText) {
          stickyText.textContent = mainText.textContent;
        }

        if (this.mainSubmitButton.disabled) {
          this.submitButton.setAttribute('disabled', 'disabled');
        } else {
          this.submitButton.removeAttribute('disabled');
        }

        const ariaDisabled = this.mainSubmitButton.getAttribute('aria-disabled');
        if (ariaDisabled === 'true') {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }

        const isLoading = this.mainSubmitButton.classList.contains('loading');
        this.submitButton.classList.toggle('loading', isLoading);

        if (mainSpinner && stickySpinner) {
          stickySpinner.classList.toggle('hidden', mainSpinner.classList.contains('hidden'));
        }
      }

      setVisibleState(visible) {
        if (this.isVisible === visible) return;

        let counter = 0;
        this.isVisible = visible;

        const t = window.setInterval(() => {
          counter++;
          if (counter > 12) {
            clearInterval(t);
            return;
          }
          if (this.mainSubmitButton && this.mainSubmitButton.offsetWidth > 0) {
            clearInterval(t);
            const mainSubmitButtonOffsetTop = this.getAbsoluteTop(this.mainSubmitButton);
            if (window.scrollY <= mainSubmitButtonOffsetTop || mainSubmitButtonOffsetTop === 0) {
              visible = false;
            }
            this.isVisible = visible;
            this.classList.toggle('sticky-atc--visible', visible);
            this.setAttribute('aria-hidden', visible ? 'false' : 'true');
            this.setBodyVisibleState(visible);
            this.classList.toggle('hidden', !visible);
            if (visible) {
              this.syncHeaderOffset();
              this.syncSubmitButtonWidth();
            }
          }
        }, 300);
      }

      setBodyVisibleState(visible) {
        const isMobile = window.matchMedia('(max-width: 1199px)').matches;
        document.body.classList.toggle('sticky-atc-visible', visible && isMobile);
      }
    }
  );
}
